import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Habitational Supplement — Multifamily / Apartments / Condo / HOA
// Source: Centered Partners revised client intake (2026-06-11), Tab 2
// "About You & Your Portfolio".
//
// The property list is deliberately simple: detailed COPE data (year built,
// construction, values) is pulled from policies and public records, then
// sent back for confirmation. Screening / Section 8 / security / amenities
// are asked in the shared operations profile (operations-profile.ts).
// Habitability and lawsuit history remain agent-mode underwriting questions.
// ---------------------------------------------------------------------------

const HAB_CONDITION = [{ field: 'industry_type', operator: 'eq' as const, value: 'habitational' }];

export const habitationalQuestions: Question[] = [
  // ---------------------------------------------------------------------
  // Key contacts — shared with MH
  // ---------------------------------------------------------------------
  {
    id: 'hab_contacts',
    text: 'Who should we work with?',
    subtext: 'Add the people we may need to reach — owner, operations lead, accounting.',
    section: 'contact',
    group: 'hab_contacts',
    sort_order: 500,
    field_type: 'repeating_group',
    required: true,
    field_path: 'contact.key_contacts',
    conditions: HAB_MH_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 8,
      add_button_label: 'Add another contact',
      row_label: 'Contact',
      fields: [
        {
          id: 'hab_kc_role',
          text: 'Role',
          section: 'contact',
          sort_order: 10,
          field_type: 'select',
          options: [
            { label: 'Principal / Owner', value: 'principal' },
            { label: 'Operations / property-management lead', value: 'operations' },
            { label: 'Accounting / CFO', value: 'accounting' },
            { label: 'Best contact for insurance questions', value: 'insurance' },
            { label: 'Other', value: 'other' },
          ],
          required: true,
          field_path: 'role',
          conditions: [],
        },
        {
          id: 'hab_kc_name',
          text: 'Name',
          section: 'contact',
          sort_order: 20,
          field_type: 'text',
          placeholder: 'Jane Smith',
          required: true,
          field_path: 'name',
          conditions: [],
        },
        {
          id: 'hab_kc_contact',
          text: 'Email / phone',
          section: 'contact',
          sort_order: 30,
          field_type: 'text',
          placeholder: 'jane@example.com · (314) 555-1234',
          required: true,
          field_path: 'email_phone',
          conditions: [],
        },
      ],
    },
    tags: ['habitational', 'contact', 'repeating'],
  },
  {
    id: 'hab_related_entities',
    text: 'Any related entities that should be named?',
    subtext: 'Operating company, holding company, affiliated LLCs.',
    section: 'business_info',
    group: 'hab_entity',
    sort_order: 500,
    field_type: 'text',
    placeholder: 'Oak Meadows OpCo LLC; OM Holdings LLC',
    required: false,
    field_path: 'business.related_entities',
    conditions: HAB_MH_CONDITION,
    tags: ['habitational', 'business_info'],
  },

  // ---------------------------------------------------------------------
  // Your properties — simple list, shared with MH (one row per property)
  // ---------------------------------------------------------------------
  {
    id: 'hab_portfolio',
    text: 'Just list your properties',
    subtext:
      'Detailed data (year built, construction, values) we pull from your policies and public records, then send back for you to confirm.',
    section: 'supplement',
    group: 'hab_portfolio',
    sort_order: 100,
    field_type: 'repeating_group',
    required: true,
    field_path: 'supplement.habitational.portfolio',
    conditions: HAB_MH_CONDITION,
    repeating_group: {
      min_rows: 1,
      max_rows: 50,
      add_button_label: 'Add another property',
      row_label: 'Property',
      fields: [
        {
          id: 'hab_p_name',
          text: 'Property name',
          section: 'supplement',
          sort_order: 10,
          field_type: 'text',
          placeholder: 'Maple Court Apartments',
          required: true,
          field_path: 'property_name',
          conditions: [],
        },
        {
          id: 'hab_p_city_state',
          text: 'City, State',
          section: 'supplement',
          sort_order: 20,
          field_type: 'text',
          placeholder: 'St. Louis, MO',
          required: true,
          field_path: 'city_state',
          conditions: [],
        },
        {
          id: 'hab_p_type',
          text: 'Type',
          section: 'supplement',
          sort_order: 30,
          field_type: 'select',
          options: [
            { label: 'Multifamily (MF)', value: 'mf' },
            { label: 'Manufactured housing (MH)', value: 'mh' },
            { label: 'Mixed', value: 'mixed' },
          ],
          required: true,
          field_path: 'property_type',
          conditions: [],
        },
        {
          id: 'hab_p_units',
          text: '# Units or pads',
          section: 'supplement',
          sort_order: 40,
          field_type: 'number',
          placeholder: '120',
          required: true,
          field_path: 'units_pads',
          conditions: [],
        },
        {
          id: 'hab_p_changes',
          text: 'Anything changing this year?',
          subtext: 'Buying, selling, expanding.',
          section: 'supplement',
          sort_order: 50,
          field_type: 'text',
          placeholder: 'Adding 30 pads in Q3',
          required: false,
          field_path: 'changes_this_year',
          conditions: [],
        },
      ],
    },
    tags: ['supplement', 'habitational', 'repeating'],
  },

  // ---------------------------------------------------------------------
  // Agent-mode underwriting history (not on the client-facing form)
  // ---------------------------------------------------------------------
  {
    id: 'hab_habitability_history',
    text: 'Any habitability, mold, or lead claims or remediation in the last 5 years?',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 230,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.habitational.habitability_history',
    conditions: HAB_CONDITION,
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
  {
    id: 'hab_habitability_detail',
    text: 'Describe the claims or remediation history',
    subtext: 'Include remediation documentation status and whether any buildings are pre-1978.',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 240,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.habitational.habitability_detail',
    conditions: [
      ...HAB_CONDITION,
      { field: 'hab_habitability_history', operator: 'eq', value: true },
    ],
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
  {
    id: 'hab_crime_incidents',
    text: 'Any criminal incidents on property grounds in the last 3 years?',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 250,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.habitational.crime_incidents',
    conditions: HAB_CONDITION,
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
  {
    id: 'hab_crime_detail',
    text: 'Describe the incidents and area crime levels',
    subtext: 'CrimeMapping or similar — last 12-month incident counts within 1 mile.',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 260,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.habitational.crime_detail',
    conditions: [...HAB_CONDITION, { field: 'hab_crime_incidents', operator: 'eq', value: true }],
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
  {
    id: 'hab_lawsuits',
    text: 'Any tenant lawsuits or HUD complaints in the last 5 years?',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 270,
    field_type: 'boolean',
    required: true,
    field_path: 'supplement.habitational.lawsuits',
    conditions: HAB_CONDITION,
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
  {
    id: 'hab_lawsuits_detail',
    text: 'Describe the lawsuits or complaints',
    subtext: 'Open and closed matters, with outcomes where resolved.',
    section: 'supplement',
    group: 'hab_history',
    sort_order: 280,
    field_type: 'textarea',
    required: true,
    field_path: 'supplement.habitational.lawsuits_detail',
    conditions: [...HAB_CONDITION, { field: 'hab_lawsuits', operator: 'eq', value: true }],
    modes: ['agent'],
    tags: ['supplement', 'habitational', 'agent'],
  },
];
